import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { cronFromForm, formFromCron, type RepeatKind } from '@brao/shared';
import { processesApi, type CreateProcessInput, type ProcessProject, type ProcessRow, type ProcessRun } from '../api/processes';
import type { TaskBusiness, TaskMember, TaskPriority } from '../api/tasks';
import { Table, type Column } from '../components/Table';
import { t } from '../i18n';
import { Link } from '../router';
import { fmtWhen, priorityLabel, statusLabel } from './tasks.format';
import './processes.css';

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;
const MONTHDAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const REPEATS: RepeatKind[] = ['weekly', 'daily', 'monthly', 'custom'];

/** "завтра 08:30" on top of fmtWhen's today / yesterday / dd.mm hh:mm. */
function fmtNext(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (d.getFullYear() === tomorrow.getFullYear() && d.getMonth() === tomorrow.getMonth() && d.getDate() === tomorrow.getDate()) {
    return `${t('processes.tomorrow')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  return fmtWhen(iso);
}

const scheduleWords = (p: ProcessRow): string => t(p.scheduleText.key, p.scheduleText.params);

function LastRunBadge({ run }: { run: ProcessRun | null }) {
  if (!run) return <span className="muted">—</span>;
  const when = fmtWhen(run.scheduledAt, false);
  return run.overdue ? <span className="badge warn">{t('processes.last.overdue', { when })}</span> : <span className="badge ok">{t('processes.last.task', { when, key: run.key })}</span>;
}

export function Processes({ canManage }: { canManage: boolean }) {
  const [businesses, setBusinesses] = useState<TaskBusiness[] | null>(null);
  const [rows, setRows] = useState<ProcessRow[] | null>(null);
  const [selected, setSelected] = useState<ProcessRow | 'new' | null>(null);
  const [runs, setRuns] = useState<ProcessRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  const load = () => {
    setError(null);
    return processesApi
      .list()
      .then((r) => {
        setRows(r);
        // Keep the open form in sync with what the server holds (next run, last run, enabled).
        setSelected((s) => (s && s !== 'new' ? (r.find((x) => x.id === s.id) ?? null) : s));
      })
      .catch((e: Error) => setError(e.message));
  };

  useEffect(() => {
    processesApi.businesses().then(setBusinesses).catch((e: Error) => setError(e.message));
    void load();
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, []);

  const selectedId = selected && selected !== 'new' ? selected.id : null;
  useEffect(() => {
    setRuns(null);
    if (!selectedId) return;
    processesApi.runs(selectedId).then(setRuns).catch((e: Error) => setError(e.message));
  }, [selectedId]);

  const reloadRuns = () => {
    if (selectedId) processesApi.runs(selectedId).then(setRuns).catch((e: Error) => setError(e.message));
  };

  const toggle = async (p: ProcessRow) => {
    setError(null);
    try {
      await (p.enabled ? processesApi.disable(p.id) : processesApi.enable(p.id));
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.error'));
    }
  };

  const runNow = async (p: ProcessRow) => {
    setError(null);
    setNotice(null);
    try {
      await processesApi.run(p.id);
      setNotice(t('processes.runQueued'));
      // The worker fires within its next poll; refresh once it has had a moment.
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        void load();
        reloadRuns();
      }, 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.error'));
    }
  };

  const activeBusinesses = useMemo(() => (businesses ?? []).filter((b) => b.status === 'active'), [businesses]);

  const columns: Column<ProcessRow>[] = [
    {
      key: 'name',
      title: t('processes.col.name'),
      render: (r) => (
        <button type="button" className="row-select" style={{ all: 'unset', cursor: 'pointer', color: 'inherit' }} onClick={() => setSelected(r)} aria-pressed={selectedId === r.id}>
          {selectedId === r.id ? <strong>{r.name}</strong> : r.name}
        </button>
      ),
      sortValue: (r) => r.name,
      wrap: true,
    },
    { key: 'business', title: t('processes.col.business'), render: (r) => r.business.name, sortValue: (r) => r.business.name },
    { key: 'schedule', title: t('processes.col.schedule'), render: (r) => scheduleWords(r), wrap: true },
    { key: 'next', title: t('processes.col.next'), render: (r) => (r.enabled && r.nextRunAt ? fmtNext(r.nextRunAt) : <span className="muted">—</span>), sortValue: (r) => (r.enabled && r.nextRunAt ? r.nextRunAt : null) },
    { key: 'last', title: t('processes.col.last'), render: (r) => <LastRunBadge run={r.lastRun} />, sortValue: (r) => r.lastRun?.scheduledAt ?? null },
    { key: 'owner', title: t('processes.col.owner'), render: (r) => r.owner?.displayName ?? <span className="muted">—</span>, sortValue: (r) => r.owner?.displayName ?? null },
    {
      key: 'enabled',
      title: t('processes.col.enabled'),
      render: (r) => <input type="checkbox" className="switch" checked={r.enabled} disabled={!canManage} onChange={() => void toggle(r)} aria-label={`${t('processes.col.enabled')}: ${r.name}`} />,
      sortValue: (r) => (r.enabled ? 1 : 0),
    },
    {
      key: 'actions',
      title: '',
      render: (r) =>
        canManage ? (
          <button type="button" className="btn secondary small" onClick={() => void runNow(r)}>
            {t('processes.runNow')}
          </button>
        ) : null,
    },
  ];

  return (
    <>
      <div className="row between">
        <h1 style={{ margin: 0 }}>{t('processes.title')}</h1>
        {canManage && (
          <button type="button" className="btn" onClick={() => setSelected('new')} disabled={activeBusinesses.length === 0}>
            {t('processes.new')}
          </button>
        )}
      </div>
      <p className="muted" style={{ margin: 'var(--sp-3) 0 var(--sp-4)' }}>{t('processes.intro')}</p>
      {businesses !== null && businesses.length === 0 && <p className="muted">{t('processes.noBusinesses')}</p>}
      {error && <div className="error" style={{ marginBottom: 'var(--sp-3)' }}>{error}</div>}
      {notice && <div className="note" style={{ marginBottom: 'var(--sp-3)' }}>{notice}</div>}

      <div className="process-table">
        <Table columns={columns} rows={rows ?? []} rowKey={(r) => r.id} empty={rows === null ? t('common.loading') : t('processes.empty')} defaultSort={{ key: 'name', dir: 'asc' }} />
      </div>

      {selected && (
        <ProcessForm
          key={selected === 'new' ? 'new' : `${selected.id}:${selected.updatedAt}`}
          process={selected === 'new' ? null : selected}
          businesses={activeBusinesses}
          canManage={canManage}
          onSaved={(p) => {
            setSelected(p);
            void load();
          }}
          onToggle={(p) => void toggle(p)}
          onClose={() => setSelected(null)}
        />
      )}

      {selected && selected !== 'new' && (
        <>
          <h2>{t('processes.runs.title')}</h2>
          <RunsTable runs={runs} />
        </>
      )}
    </>
  );
}

function RunsTable({ runs }: { runs: ProcessRun[] | null }) {
  const columns: Column<ProcessRun>[] = [
    {
      key: 'scheduled',
      title: t('processes.runs.col.scheduled'),
      render: (r) => (
        <>
          {fmtWhen(r.scheduledAt)}
          {r.manual && <span className="muted"> · {t('processes.runs.manual')}</span>}
        </>
      ),
      sortValue: (r) => r.scheduledAt,
    },
    { key: 'created', title: t('processes.runs.col.created'), render: (r) => fmtWhen(r.createdAt), sortValue: (r) => r.createdAt },
    { key: 'task', title: t('processes.runs.col.task'), render: (r) => <Link to={`/tasks/${r.id}`}>{r.key}</Link>, sortValue: (r) => r.key },
    {
      key: 'status',
      title: t('processes.runs.col.status'),
      render: (r) => (
        <>
          <span className={r.status === 'done' ? 'badge ok' : 'badge'}>{statusLabel(r.status)}</span>
          {r.overdue && <span className="badge warn" style={{ marginLeft: 'var(--sp-1)' }}>{t('processes.overdue')}</span>}
        </>
      ),
      sortValue: (r) => r.status,
    },
    { key: 'closed', title: t('processes.runs.col.closed'), render: (r) => (r.closedAt ? fmtWhen(r.closedAt) : <span className="muted">—</span>), sortValue: (r) => r.closedAt },
  ];
  return <Table columns={columns} rows={runs ?? []} rowKey={(r) => r.id} empty={runs === null ? t('common.loading') : t('processes.runs.empty')} />;
}

function ProcessForm({ process, businesses, canManage, onSaved, onToggle, onClose }: { process: ProcessRow | null; businesses: TaskBusiness[]; canManage: boolean; onSaved: (p: ProcessRow) => void; onToggle: (p: ProcessRow) => void; onClose: () => void }) {
  const initial = formFromCron(process?.schedule);
  const [businessId, setBusinessId] = useState(process?.businessId ?? businesses[0]?.id ?? '');
  const [name, setName] = useState(process?.name ?? '');
  const [repeat, setRepeat] = useState<RepeatKind>(initial.repeat);
  const [day, setDay] = useState<number>(initial.day ?? 1);
  const [time, setTime] = useState(initial.time ?? '09:00');
  const [customCron, setCustomCron] = useState(initial.cron ?? '');
  const [timezone, setTimezone] = useState(process?.timezone ?? businesses.find((b) => b.id === businessId)?.timezone ?? 'Europe/Kyiv');
  const [sla, setSla] = useState(process?.slaMinutes ? String(process.slaMinutes) : '');
  const [ownerId, setOwnerId] = useState(process?.ownerId ?? '');
  const [title, setTitle] = useState(process?.taskTemplate?.title ?? '');
  const [description, setDescription] = useState(process?.taskTemplate?.description ?? '');
  const [assigneeId, setAssigneeId] = useState(process?.taskTemplate?.assigneeId ?? '');
  const [priority, setPriority] = useState<TaskPriority>(process?.taskTemplate?.priority ?? 'normal');
  const [projectId, setProjectId] = useState(process?.taskTemplate?.projectId ?? '');
  const [members, setMembers] = useState<TaskMember[]>([]);
  const [projects, setProjects] = useState<ProcessProject[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  // The timezone of a new process follows the business until edited by hand; an existing one keeps its own.
  useEffect(() => {
    if (!process) setTimezone(businesses.find((b) => b.id === businessId)?.timezone ?? 'Europe/Kyiv');
  }, [businessId, businesses, process]);

  // Owner / assignee candidates and projects belong to the chosen business.
  useEffect(() => {
    if (!businessId) return;
    processesApi.members(businessId).then(setMembers).catch((e: Error) => setError(e.message));
    processesApi.projects(businessId).then(setProjects).catch((e: Error) => setError(e.message));
  }, [businessId]);

  const dayOptions = repeat === 'weekly' ? WEEKDAYS.map((d) => ({ value: d, label: t(`processes.weekday.${d}`) })) : repeat === 'monthly' ? MONTHDAYS.map((d) => ({ value: d, label: t('processes.monthday', { day: d }) })) : [];
  useEffect(() => {
    if (repeat === 'weekly' && day > 7) setDay(1);
  }, [repeat, day]);

  const cron = cronFromForm(repeat === 'custom' ? { repeat, cron: customCron } : { repeat, day, time });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!cron) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    const taskTemplate = { title: title.trim(), description: description.trim() || null, priority, assigneeId: assigneeId || null, projectId: projectId || null };
    const common = { name: name.trim(), schedule: cron, timezone: timezone.trim(), slaMinutes: sla.trim() ? Number(sla) : null, ownerId: ownerId || null, taskTemplate };
    try {
      const result = process ? await processesApi.update(process.id, common) : await processesApi.create({ businessId, ...common } satisfies CreateProcessInput);
      setSaved(true);
      onSaved(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  const ro = !canManage;

  return (
    <>
      <h2>{process ? t('processes.edit.title', { name: process.name }) : t('processes.edit.new')}</h2>
      <form className="process-form" onSubmit={submit} aria-label={process ? t('processes.edit.title', { name: process.name }) : t('processes.edit.new')}>
        <div className="grid2">
          <div className="card stack">
            <label className="field">
              {t('processes.form.name')}
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} readOnly={ro} />
            </label>
            <label className="field">
              {t('processes.form.business')}
              <select value={businessId} onChange={(e) => setBusinessId(e.target.value)} disabled={!!process || ro} required>
                {(process ? [{ id: process.businessId, name: process.business.name }] : businesses).map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </label>
            <div className="row">
              <label className="field">
                {t('processes.form.repeat')}
                <select value={repeat} onChange={(e) => setRepeat(e.target.value as RepeatKind)} disabled={ro}>
                  {REPEATS.map((r) => (
                    <option key={r} value={r}>{t(`processes.repeat.${r}`)}</option>
                  ))}
                </select>
              </label>
              {dayOptions.length > 0 && (
                <label className="field">
                  {t('processes.form.day')}
                  <select value={day} onChange={(e) => setDay(Number(e.target.value))} disabled={ro}>
                    {dayOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>
              )}
              {repeat !== 'custom' && (
                <label className="field">
                  {t('processes.form.time')}
                  <input className="input" type="time" value={time} onChange={(e) => setTime(e.target.value)} required readOnly={ro} />
                </label>
              )}
            </div>
            {repeat === 'custom' && (
              <label className="field">
                {t('processes.form.cron')}
                <input className="input" value={customCron} onChange={(e) => setCustomCron(e.target.value)} placeholder="0 9 * * 1-5" required readOnly={ro} />
              </label>
            )}
            <div className="cron-line">{cron ? t('processes.form.cronLine', { cron, tz: timezone }) : <span className="error">{t('processes.form.cron')}</span>}</div>
            <label className="field">
              {t('processes.form.timezone')}
              <input className="input" value={timezone} onChange={(e) => setTimezone(e.target.value)} required readOnly={ro} />
            </label>
            <label className="field">
              {t('processes.form.sla')}
              <input className="input" type="number" min={1} value={sla} onChange={(e) => setSla(e.target.value)} readOnly={ro} inputMode="numeric" />
            </label>
            <label className="field">
              {t('processes.form.owner')}
              <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)} disabled={ro}>
                <option value="">{t('processes.form.none')}</option>
                {members.map((m) => (
                  <option key={m.actorId} value={m.actorId}>{m.displayName}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="card stack">
            <strong>{t('processes.template.title')}</strong>
            <label className="field">
              {t('processes.template.name')}
              <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={300} readOnly={ro} />
            </label>
            <label className="field">
              {t('processes.template.description')}
              <textarea className="input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} readOnly={ro} />
            </label>
            <div className="row">
              <label className="field">
                {t('processes.template.assignee')}
                <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} disabled={ro}>
                  <option value="">{t('processes.form.none')}</option>
                  {members.map((m) => (
                    <option key={m.actorId} value={m.actorId}>{m.displayName}</option>
                  ))}
                </select>
              </label>
              <label className="field">
                {t('processes.template.priority')}
                <select value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)} disabled={ro}>
                  {(['normal', 'high', 'urgent', 'low'] as TaskPriority[]).map((p) => (
                    <option key={p} value={p}>{priorityLabel(p)}</option>
                  ))}
                </select>
              </label>
            </div>
            {projects.length > 0 && (
              <label className="field">
                {t('processes.template.project')}
                <select value={projectId} onChange={(e) => setProjectId(e.target.value)} disabled={ro}>
                  <option value="">{t('processes.form.none')}</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </label>
            )}
            <div className="note">{t('processes.template.hint')}</div>
            {error && <div className="error">{error}</div>}
            {saved && !error && <div className="ok">{t('processes.form.saved')}</div>}
            <div className="row">
              {canManage && (
                <button className="btn" type="submit" disabled={busy || !cron || !businessId}>
                  {process ? t('processes.form.save') : t('processes.form.create')}
                </button>
              )}
              {canManage && process && (
                <button className="btn secondary" type="button" onClick={() => onToggle(process)}>
                  {process.enabled ? t('processes.form.disable') : t('processes.form.enable')}
                </button>
              )}
              <button className="btn secondary" type="button" onClick={onClose}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}
